export const METRICS = ['IMPRESSION', 'OUTBOUND_CLICK', 'SAVE', 'PIN_CLICK'];
export const LABELS = { IMPRESSION: 'Impressions', OUTBOUND_CLICK: 'Outbound Clicks', SAVE: 'Saves', PIN_CLICK: 'Pin Clicks', CTR: 'Outbound CTR' };
export const DIAGNOSES = { WINNER: 'Winners', LOST_TRAFFIC: 'Lost Traffic', HIGH_IMPRESSIONS_LOW_CTR: 'High Impressions / Low CTR', LOW_DISTRIBUTION: 'Low Distribution', RECOVERY: 'Recovery Opportunities' };
const DAY = 86400000;
export const isoDay = date => new Date(date).toISOString().slice(0, 10);
export const shiftDay = (date, days) => isoDay(Date.parse(date + 'T00:00:00Z') + days * DAY);
export function periodFor(days, today = isoDay(Date.now())) {
  return { start: shiftDay(today, -days), end: shiftDay(today, -1) };
}
export function validPeriod(period, today = isoDay(Date.now())) {
  if (!period || ![period.start, period.end].every(s => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) && Number.isFinite(Date.parse(s)) && isoDay(Date.parse(s)) === s)) return false;
  return period.start >= shiftDay(today, -90) && period.end < today && period.start <= period.end && (Date.parse(period.end) - Date.parse(period.start)) / DAY < 90;
}
export function previousPeriod(period) {
  const days = (Date.parse(period.end) - Date.parse(period.start)) / DAY + 1;
  return { start: shiftDay(period.start, -days), end: shiftDay(period.start, -1) };
}
export const metric = value => typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
export const ctr = (clicks, impressions) => metric(clicks) === null || metric(impressions) === null ? null : impressions === 0 ? 0 : clicks / impressions * 100;
export const change = (current, previous) => metric(current) === null || metric(previous) === null || previous === 0 ? null : (current - previous) / previous * 100;
/** @returns {Record<string, number | null>} */
export function metricsFromRaw(raw) {
  const summary = raw?.all?.summary_metrics;
  const values = Object.fromEntries(METRICS.map(key => [key, metric(summary?.[key])]));
  return { ...values, CTR: ctr(values.OUTBOUND_CLICK, values.IMPRESSION) };
}
// Linear-interpolated percentiles; boundary ties are included. Missing values are excluded.
export function quantile(values, p) {
  const sorted = values.filter(v => metric(v) !== null).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const position = (sorted.length - 1) * p, lower = Math.floor(position);
  return sorted[lower] + (sorted[Math.ceil(position)] - sorted[lower]) * (position - lower);
}
export function diagnose(rows) {
  const eligible = rows.filter(r => r.current.IMPRESSION > 0 && r.current.CTR !== null);
  const topCTR = quantile(eligible.map(r => r.current.CTR), .75);
  const lowCTR = quantile(eligible.map(r => r.current.CTR), .25);
  const topClicks = quantile(eligible.map(r => r.current.OUTBOUND_CLICK), .75);
  const topImpressions = quantile(rows.map(r => r.current.IMPRESSION), .75);
  const previousMedian = quantile(rows.filter(r => r.previous.IMPRESSION > 0).map(r => r.previous.CTR), .5);
  return rows.map(row => {
    const c = row.current, p = row.previous, reasons = {};
    if (c.IMPRESSION > 0 && c.OUTBOUND_CLICK > 0 && c.CTR !== null && topCTR !== null && topClicks !== null && c.CTR >= topCTR && c.OUTBOUND_CLICK >= topClicks)
      reasons.WINNER = 'Outbound clicks and CTR are at or above the account’s 75th percentiles, with positive outbound clicks.';
    if (c.IMPRESSION > 0 && topImpressions !== null && lowCTR !== null && c.CTR !== null && c.IMPRESSION >= topImpressions && c.CTR <= lowCTR)
      reasons.HIGH_IMPRESSIONS_LOW_CTR = 'Impressions are at or above the account’s 75th percentile; CTR is at or below the 25th percentile among Pins with impressions.';
    if (p.IMPRESSION > 0 && c.IMPRESSION !== null && c.IMPRESSION <= p.IMPRESSION * .5)
      reasons.LOW_DISTRIBUTION = 'Impressions decreased by at least 50% from a positive previous period.';
    if (p.OUTBOUND_CLICK > 0 && c.OUTBOUND_CLICK !== null && c.OUTBOUND_CLICK <= p.OUTBOUND_CLICK * .5)
      reasons.LOST_TRAFFIC = 'Outbound clicks decreased by at least 50% from a positive previous period.';
    if ((reasons.LOST_TRAFFIC || reasons.LOW_DISTRIBUTION) && previousMedian !== null && p.CTR !== null && p.IMPRESSION > 0 && p.CTR >= previousMedian)
      reasons.RECOVERY = 'Traffic or distribution declined and previous CTR was at or above the account’s previous-period median.';
    return { ...row, reasons, outboundChange: change(c.OUTBOUND_CLICK, p.OUTBOUND_CLICK), impressionChange: change(c.IMPRESSION, p.IMPRESSION) };
  });
}
/** @returns {Record<string, number | null>} */
export function summarize(rows, period) {
  // Incomplete coverage is N/A, never a misleading partial account total.
  const values = Object.fromEntries(METRICS.map(key => [key, rows.length && rows.every(r => metric(r[period][key]) !== null) ? rows.reduce((sum, r) => sum + r[period][key], 0) : null]));
  return { ...values, CTR: ctr(values.OUTBOUND_CLICK, values.IMPRESSION) };
}
